<template>
    <ModalContent stitch :help-content-id="`popup-billingaccount`">
        <template slot="title">{{ $translate("title") }}</template>
        <template slot="content" v-if="billingaccount">
            <Info :elevation="3"> {{ $translate("instructions") }} </Info>
            <h6 class="stitch-modal__title">Information</h6>
            <v-row>
                <v-col cols="12" sm="4">
                    <v-text-field
                        class="pt-0"
                        v-model="billingaccount.siren"
                        autofocus
                        :error-messages="billingaccount_saving_errors?.siren"
                        :label="$translate('siren')"
                     outlined  dense />
                </v-col>
                <v-col cols="12" sm="4">
                    <v-select
                        :label="$translate('legal_structure')"
                        class="pt-0"
                        :error-messages="billingaccount_saving_errors.legal_structure"
                        :items="$utils.getLegalStructureItems()"
                        :value="getLegalStructure(billingaccount.legal_structure)"
                        @input="billingaccount.legal_structure = $event"
                     outlined  dense />
                </v-col>
                <v-col
                    cols="12"
                    sm="4"
                    v-if="$utils.shareCapitalRequired(billingaccount.legal_structure)"
                >
                    <v-text-field
                        class="pt-0"
                        v-model="billingaccount.share_capital"
                        :error-messages="billingaccount_saving_errors?.share_capital"
                        :label="$translate('share_capital')"
                     outlined  dense />
                </v-col>
            </v-row>
            <h6 class="stitch-modal__title">
                {{ $translate("address") }}
            </h6>
            <v-row>
                <v-col cols="12" sm="6">
                    <v-text-field
                        class="pt-0"
                        v-model="billingaccount.name"
                        :error-messages="billingaccount_saving_errors?.name"
                        :label="$translate('legalName')"
                     outlined  dense />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-combobox
                        v-model="billingaccount.address.emails"
                        multiple
                        clearable
                        :label="$translate('emails')"
                        class="pt-0"
                        dense
                        deletable-chips

                        :error-messages="billingaccount_saving_errors?.address?.emails"
                     outlined ><template #label><span>{{ $translate('emails') }}</span><StitchHint :hint="$translate('emailHint')" /></template></v-combobox>
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        class="pt-0"
                        v-model="billingaccount.address.line1"
                        :error-messages="billingaccount_saving_errors?.address?.line1"
                        :label="$translate('line1')"
                     outlined  dense />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        class="pt-0"
                        v-model="billingaccount.address.line2"
                        :error-messages="billingaccount_saving_errors?.address?.line2"
                        :label="$translate('line2')"
                     outlined  dense />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        class="pt-0"
                        v-model="billingaccount.address.city"
                        :error-messages="billingaccount_saving_errors?.address?.city"
                        :label="$translate('city')"
                     outlined  dense />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="billingaccount.address.postal_code"
                        :error-messages="
                            billingaccount_saving_errors?.address?.postal_code
                        "
                        :label="$translate('postalCode')"
                     outlined  dense />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="billingaccount.address.country"
                        :error-messages="billingaccount_saving_errors?.address?.country"
                        :label="$translate('country')"
                     outlined  dense />
                </v-col>
            </v-row>
        </template>
        <template slot="bottom">
            <template v-if="!is_readonly">
                <Button
                    class="my-2 my-sm-4 w-100 w-sm-auto"
                    ghost
                    :text="$translate('cancel')"
                    @click="hideModal"
                />
                <Button
                    class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                    :text="$translate('continue')"
                    :disabled="!canSave || billingaccount_saving"
                    :loading="billingaccount_saving"
                    @click="saveInformationClicked"
                />
            </template>
            <template v-else>
                <Button
                    ghost
                    :text="$translate('close')"
                    @click="hideModal"
                    class="my-2 my-sm-4 w-100 w-sm-auto"
                />
            </template>
        </template>
    </ModalContent>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import GlobalMixins from "@/mixins/global";

export default {
    mixins: [GlobalMixins],
    computed: {
        ...mapState("editbillingaccount", [
            "billingaccount",
            "billingaccount_saving",
            "billingaccount_saving_errors",
        ]),
        canSave() {
            return (
                this.billingaccount.siren &&
                this.billingaccount.address?.city &&
                this.billingaccount.address?.country &&
                this.billingaccount.address?.emails &&
                this.billingaccount.address?.line1 &&
                this.billingaccount.address?.postal_code
            );
        },
    },
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    created() {
        this.init({ billingaccount: this.params.billingaccount });
    },
    methods: {
        ...mapMutations("editbillingaccount", ["updateBillingAccount"]),
        ...mapActions("editbillingaccount", ["saveBillingAccount", "init"]),
        getLegalStructure(value) {
            return this.$utils
                .getLegalStructureItems()
                .find((item) => item.value === value);
        },

        async saveInformationClicked() {
            try {
                const response = await this.saveBillingAccount({
                    instance: this.billingaccount,
                });
                this.hideModal({ result: response.data });
            } catch (error) {
                this.$swal({
                    title: this.$translate("failed"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            }
        },
    },
    i18n: {
        messages: {
            en: {
                title: "Complete your informations",
                instructions:
                    "Please complete the billing account informations assigned to the customer",
                failed: "Failed saving informations",
                save: "Save informations",
                siren: "Your company SIREN",
                emails: "Contact email(s)",
                line1: "Address",
                line2: "Address complement",
                city: "City",
                postalCode: "Postal code",
                country: "Country",
                continue: "Continue",
                required: "This field is required",
                legalName: "Legal name",
                legal_structure: "Legal structure",
                share_capital: "Share capital",
                address: "Address",
                emailHint: "Enter email and press TAB key to add email.",
                cancel: "Cancel",
                close: "Close",
            },
            fr: {
                title: "Complétez vos informations",
                instructions:
                    "Veuillez compléter les informations du compte de facturation.",
                failed: "Échec de l'enregistrement des informations",
                save: "Enregistrer les informations",
                siren: "SIREN de votre entreprise",
                emails: "Email(s) de contact",
                line1: "Adresse",
                line2: "Complément d'adresse",
                city: "Ville",
                postalCode: "Code postal",
                country: "Pays",
                continue: "Continuer",
                required: "Ce champ est requis",
                legalName: "Raison sociale",
                legal_structure: "Structure juridique",
                share_capital: "Capital social",
                address: "Adresse",
                emailHint:
                    "Entrez l'adresse e-mail à ajouter puis appuyez sur la touche TAB pour en ajouter une autre.",
                cancel: "Annuler",
                close: "Fermer",
            },
        },
    },
};
</script>
